import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { payrollAPI } from '../../services/api';
import { useToast } from '../Toast';
import { XMarkIcon } from '@heroicons/react/24/outline';

const PayrollEditModal = ({ payroll, onClose, onSuccess }) => {
  const { showToast } = useToast();
  const [formData, setFormData] = useState({
    days_worked: payroll.days_worked,
    gross_salary: payroll.gross_salary,
    leave_deduction: payroll.leave_deduction,
    net_salary: payroll.net_salary,
    remarks: payroll.remarks || '',
  });

  const updateMutation = useMutation({
    mutationFn: (data) => payrollAPI.update(payroll.payroll_id, data),
    onSuccess: () => {
      showToast('Payroll updated successfully', 'success');
      onSuccess();
    },
    onError: (error) => {
      showToast(error.response?.data?.detail || 'Failed to update payroll', 'error');
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    const numericFields = ['days_worked', 'gross_salary', 'leave_deduction', 'net_salary'];

    setFormData((prev) => ({
      ...prev,
      [name]: numericFields.includes(name) ? parseFloat(value) || 0 : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getMonthName = (month) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[month - 1];
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-xl shadow-2xl p-6 w-full max-w-md border border-gray-700">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white">
            Edit Payroll - {getMonthName(payroll.month)} {payroll.year}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Days Worked */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Days Worked (out of {payroll.working_days_in_month})
            </label>
            <input
              type="number"
              name="days_worked"
              value={formData.days_worked}
              onChange={handleChange}
              min="0"
              max={payroll.working_days_in_month}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Gross Salary */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Gross Salary
            </label>
            <input
              type="number"
              name="gross_salary"
              value={formData.gross_salary}
              onChange={handleChange}
              min="0"
              step="0.01"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Deductions */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Deductions
            </label>
            <input
              type="number"
              name="leave_deduction"
              value={formData.leave_deduction}
              onChange={handleChange}
              min="0"
              step="0.01"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Net Salary */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Net Salary
            </label>
            <input
              type="number"
              name="net_salary"
              value={formData.net_salary}
              onChange={handleChange}
              min="0"
              step="0.01"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Calculated: {formatCurrency(formData.gross_salary - formData.leave_deduction)}
            </p>
          </div>

          {/* Remarks */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Remarks (optional)
            </label>
            <textarea
              name="remarks"
              value={formData.remarks}
              onChange={handleChange}
              rows="2"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Add any notes..."
            />
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PayrollEditModal;
